import React from 'react';
import { Alert, Button } from 'react-bootstrap';

/**
 * ErrorBoundary component to catch and display React errors
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="container mt-4">
          <Alert variant="danger">
            <Alert.Heading>Something went wrong</Alert.Heading>
            <p>
              An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.
            </p>
            <hr />
            <div className="d-flex justify-content-end">
              <Button 
                onClick={() => window.location.reload()} 
                variant="outline-danger"
              >
                Refresh Page
              </Button>
            </div>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * ErrorMessage component for displaying API errors
 */
export const ErrorMessage = ({ message, onRetry, className = "" }) => {
  if (!message) return null;

  return (
    <Alert variant="danger" className={className}>
      <Alert.Heading>Error</Alert.Heading>
      <p>{message}</p>
      {onRetry && (
        <>
          <hr />
          <div className="d-flex justify-content-end">
            <Button onClick={onRetry} variant="outline-danger">
              Try Again
            </Button>
          </div>
        </>
      )}
    </Alert>
  );
};

/**
 * LoadingSpinner component for showing loading states
 */
export const LoadingSpinner = ({ message = "Loading..." }) => (
  <div className="d-flex justify-content-center align-items-center p-4">
    <div className="spinner-border me-3" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
    <span>{message}</span>
  </div>
);

/**
 * EmptyState component for showing empty data states
 */
export const EmptyState = ({ 
  title = "No data available", 
  description = "There's nothing to display at the moment.",
  icon = "📭"
}) => (
  <div className="text-center py-5">
    <div className="display-1 mb-3">{icon}</div>
    <h4 className="text-muted">{title}</h4>
    <p className="text-muted">{description}</p>
  </div>
);

/**
 * ConnectionError component for network-related errors
 */
export const ConnectionError = ({ onRetry }) => (
  <ErrorMessage 
    message="Unable to connect to the server. Please check your internet connection and try again."
    onRetry={onRetry}
  />
);

/**
 * NotificationAlert component for temporary notifications
 */
export const NotificationAlert = ({ 
  variant = "info", 
  message, 
  show = true, 
  onClose, 
  dismissible = true 
}) => {
  if (!show || !message) return null;

  return (
    <Alert 
      variant={variant} 
      onClose={onClose} 
      dismissible={dismissible}
      className="position-fixed top-0 end-0 m-3"
      style={{ zIndex: 1050 }}
    >
      {message}
    </Alert>
  );
};

export default ErrorBoundary;
